import { useMemo } from 'react';

import { DEMO_USERS_DATA } from 'pages/period-close/checklist-v2/demo/generateDemoData';

import { useUserAccessAndRoles } from 'hooks/auth/useUserAccessAndRoles';

import { featureFlags } from 'utils/config/featureFlags';

import { useAuthStore } from 'store/auth.store';

import type { User } from 'types/data/user';

export function useUsersData() {
  const { isLoading } = useUserAccessAndRoles();
  const { usersEntityAccessList } = useAuthStore();

  const users = useMemo(() => {
    const mapped = usersEntityAccessList
      .map(
        (user) =>
          ({
            id: user.user_id,
            name: user.user_name,
            email: user.user_email,
            photoUrl: null,
          }) as User
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    // Merge demo-only users when the demo flag is on
    if (featureFlags.FF__CLOSE_DEMO_PAGES) {
      const existingIds = new Set(mapped.map((u) => u.id));
      const extras = DEMO_USERS_DATA.filter((u) => !existingIds.has(u.id));
      return [...mapped, ...extras];
    }

    return mapped;
  }, [usersEntityAccessList]);

  const usersMap: Record<string, User> = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u])),
    [users]
  );

  return {
    users,
    usersMap,
    isLoading,
  };
}
